import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

// Round 110 — Mobile Safari signs out on tab return because
//   (1) React state `user` was memory-only, so it reset when iOS
//       resumed the tab from a cached snapshot, and
//   (2) `/auth/me` failure (network abort during resume) was clearing
//       the token indiscriminately.
// Fix: hydrate `user` from localStorage synchronously so the UI keeps
// working while `/auth/me` re-validates in the background, and only
// clear the token on confirmed 401 responses (not transient errors).
const USER_LS_KEY = "adams_user";

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user) {
  try {
    if (user) localStorage.setItem(USER_LS_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_LS_KEY);
  } catch {
    // localStorage full / unavailable — safe to ignore, in-memory state still works.
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readCachedUser());
  // Only show the boot loading screen when there is NO cached user
  // AND we might have a token — otherwise we can paint immediately.
  const [loading, setLoading] = useState(() => {
    const token = localStorage.getItem("adams_token");
    return !!token && !readCachedUser();
  });

  useEffect(() => {
    const token = localStorage.getItem("adams_token");
    if (!token) {
      setLoading(false);
      return;
    }
    // Stale-while-revalidate: paint immediately from cache, then
    // validate. Only a confirmed 401 (bad/expired token) clears state.
    // Any other error (network abort, timeout, backend 5xx, iOS
    // resume) keeps the cached user visible so the app stays usable.
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        writeCachedUser(r.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("adams_token");
          writeCachedUser(null);
          setUser(null);
        }
        // else: transient — keep the cached user, /auth/me will retry
        // naturally on the next in-app navigation.
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (mobile, password) => {
    const { data } = await api.post("/auth/login", { mobile, password });
    localStorage.setItem("adams_token", data.token);
    writeCachedUser(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("adams_token");
    writeCachedUser(null);
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
